import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, CalendarPlus, Loader2, MailQuestion, MapPin, Sparkles, UserCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { useCamp } from "@/context/SettingsContext";
import { fmtDate } from "@/lib/constants";

// Teksten voor de "vraag meer info"-mail in de taal van de gast (val terug op Engels).
const ASK = {
  nl: { hi: "Beste", intro: "Om uw reservatie te kunnen bevestigen, hebben we nog de volgende gegevens nodig:", bye: "Alvast bedankt.\nMet vriendelijke groet", arrival: "Aankomstdatum", departure: "Vertrekdatum", persons: "Aantal personen", pitch: "Type standplaats", name: "Volledige naam" },
  fr: { hi: "Bonjour", intro: "Afin de confirmer votre réservation, nous avons encore besoin des informations suivantes :", bye: "Merci d'avance.\nCordialement", arrival: "Date d'arrivée", departure: "Date de départ", persons: "Nombre de personnes", pitch: "Type d'emplacement", name: "Nom complet" },
  de: { hi: "Guten Tag", intro: "Um Ihre Reservierung zu bestätigen, benötigen wir noch folgende Angaben:", bye: "Vielen Dank im Voraus.\nMit freundlichen Grüßen", arrival: "Anreisedatum", departure: "Abreisedatum", persons: "Anzahl Personen", pitch: "Art des Stellplatzes", name: "Vollständiger Name" },
  en: { hi: "Dear", intro: "To confirm your reservation we still need the following details:", bye: "Thank you in advance.\nKind regards", arrival: "Arrival date", departure: "Departure date", persons: "Number of persons", pitch: "Type of pitch", name: "Full name" },
  es: { hi: "Estimado/a", intro: "Para confirmar su reserva necesitamos los siguientes datos:", bye: "Gracias de antemano.\nUn cordial saludo", arrival: "Fecha de llegada", departure: "Fecha de salida", persons: "Número de personas", pitch: "Tipo de parcela", name: "Nombre completo" },
  it: { hi: "Gentile", intro: "Per confermare la Sua prenotazione ci servono ancora i seguenti dati:", bye: "Grazie in anticipo.\nCordiali saluti", arrival: "Data di arrivo", departure: "Data di partenza", persons: "Numero di persone", pitch: "Tipo di piazzola", name: "Nome completo" },
};

const AVAIL_STYLE = {
  green: "bg-emerald-100 text-emerald-800",
  orange: "bg-amber-100 text-amber-800",
  red: "bg-rose-100 text-rose-800",
  unknown: "bg-slate-100 text-slate-600",
};
const AVAIL_KEY = { green: "avail_green", orange: "avail_orange", red: "avail_red", unknown: "avail_unknown" };

const Field = ({ label, miss, needLabel, children }) => (
  <div className="space-y-1">
    <Label className="text-xs flex items-center gap-1.5">
      {label}
      {miss && <span className="rounded bg-amber-100 text-amber-800 px-1.5 text-[10px] font-semibold">{needLabel}</span>}
    </Label>
    {children}
  </div>
);

export default function BookingReviewDialog({ data, guest, message, onCancel, onAskInfo, onCreate }) {
  const { t } = useLang();
  const { camp } = useCamp();
  const [f, setF] = useState(() => ({
    guest_id: guest?.guest_id || "",
    first_name: data.first_name || "", last_name: data.last_name || "",
    email: data.email || "", phone: data.phone || "",
    passport_number: data.passport_number || "",
    language: guest?.language || data.language || "",
    arrival: data.arrival || "", departure: data.departure || "",
    adults: data.adults || 1, children: data.children || 0, dogs: data.dogs || 0,
    pitch_type: data.pitch_type || "", pitch_label: data.pitch_label || "", notes: data.notes || "",
  }));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (k, v) => set(k, Math.max(0, parseInt(v || 0, 10) || 0));

  // Beschikbaarheid van een gevraagde plaats: groen (vrij), oranje (mits verhuis), rood (niet beschikbaar).
  const [avail, setAvail] = useState(null); // {status, moves} | "loading" | null
  useEffect(() => {
    const label = f.pitch_label.trim();
    if (!label || !f.arrival || !f.departure || f.departure <= f.arrival) { setAvail(null); return; }
    setAvail("loading");
    const id = setTimeout(async () => {
      try {
        const { data: r } = await api.get("/pitch-availability", { params: { label, from: f.arrival, to: f.departure } });
        setAvail(r);
      } catch { setAvail(null); }
    }, 350);
    return () => clearTimeout(id);
  }, [f.pitch_label, f.arrival, f.departure]);

  const missing = useMemo(() => {
    const m = [];
    if (!f.first_name && !f.last_name) m.push("name");
    if (!f.arrival) m.push("arrival");
    if (!f.departure) m.push("departure");
    if (!f.pitch_type) m.push("pitch");
    return m;
  }, [f]);

  const build = () => {
    const bits = [`${t("mb_from_mail")}: "${message?.subject || ""}"`];
    if (f.pitch_type) bits.push(`${t("mb_pitch_type")}: ${f.pitch_type}`);
    if (f.dogs) bits.push(`${f.dogs} ${t("mb_dogs")}`);
    return {
      guest_id: f.guest_id, first_name: f.first_name, last_name: f.last_name,
      email: f.email, phone: f.phone, passport_number: f.passport_number,
      language: f.language, arrival: f.arrival, departure: f.departure,
      adults: f.adults, children: f.children, dogs: f.dogs, pitch_type: f.pitch_type,
      pitch_label: f.pitch_label.trim(),
      move_plan: (avail && avail.status === "orange" && avail.chain?.length > 1)
        ? avail.chain.map((c) => ({ pitch_label: c.pitch_label, zone: c.zone || "", arrival: c.from, departure: c.to }))
        : null,
      note: [...bits, f.notes].filter(Boolean).join(" · "),
    };
  };

  const askInfo = () => {
    const lang = (f.language || "en").slice(0, 2);
    const L = ASK[lang] || ASK.en;
    const name = f.first_name || guest?.guest_name || message?.from?.name || "";
    const labels = { name: L.name, arrival: L.arrival, departure: L.departure, pitch: L.pitch };
    const items = (missing.length ? missing : ["arrival", "departure", "pitch"]).map((k) => `- ${labels[k] || k}`);
    const body = `${L.hi} ${name},\n\n${L.intro}\n\n${items.join("\n")}\n\n${L.bye},\n${camp?.camp_name || ""}`;
    onAskInfo(body);
  };

  const needLabel = t("mb_still_needed");

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-2xl bg-background max-h-[92vh] overflow-y-auto cb-scroll" data-testid="booking-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />{t("mb_review_title")}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground -mt-1">{t("mb_review_hint")}</p>

        {!data.is_booking_request && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="booking-review-not-booking">
            <MailQuestion className="h-4 w-4 mt-0.5 shrink-0" />{t("mb_not_booking_warn")}
          </div>
        )}

        {guest?.guest_id && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-sm text-emerald-800" data-testid="booking-review-known-guest">
            <UserCheck className="h-4 w-4 shrink-0" /><span className="font-semibold">{t("mb_known_guest")}:</span> {guest.guest_name}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("first_name")} miss={missing.includes("name")} needLabel={needLabel}>
            <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="review-first-name" />
          </Field>
          <Field label={t("last_name")} miss={missing.includes("name")} needLabel={needLabel}>
            <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="review-last-name" />
          </Field>
          <Field label={t("email")}>
            <Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="review-email" />
          </Field>
          <Field label={t("phone")}>
            <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="review-phone" />
          </Field>
          <Field label={t("arrival")} miss={missing.includes("arrival")} needLabel={needLabel}>
            <Input type="date" value={f.arrival} onChange={(e) => set("arrival", e.target.value)} data-testid="review-arrival" />
          </Field>
          <Field label={t("departure")} miss={missing.includes("departure")} needLabel={needLabel}>
            <Input type="date" value={f.departure} onChange={(e) => set("departure", e.target.value)} data-testid="review-departure" />
          </Field>
          <Field label={t("stay_adults")}>
            <Input type="number" min="1" value={f.adults} onChange={(e) => num("adults", e.target.value)} data-testid="review-adults" />
          </Field>
          <Field label={t("stay_children")}>
            <Input type="number" min="0" value={f.children} onChange={(e) => num("children", e.target.value)} data-testid="review-children" />
          </Field>
          <Field label={t("stay_dogs")}>
            <Input type="number" min="0" value={f.dogs} onChange={(e) => num("dogs", e.target.value)} data-testid="review-dogs" />
          </Field>
          <Field label={t("mb_pitch_type")} miss={missing.includes("pitch")} needLabel={needLabel}>
            <Input value={f.pitch_type} onChange={(e) => set("pitch_type", e.target.value)} placeholder="kampeerplaats, caravan…" data-testid="review-pitch-type" />
          </Field>
          <Field label={t("mb_requested_pitch")}>
            <Input value={f.pitch_label} onChange={(e) => set("pitch_label", e.target.value)} placeholder="A12, 134…" data-testid="review-pitch-label" />
          </Field>
        </div>

        {f.pitch_label.trim() && (
          <div data-testid="review-availability">
            {avail === "loading" ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />{t("avail_checking")}</span>
            ) : avail ? (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${AVAIL_STYLE[avail.status] || AVAIL_STYLE.unknown}`} data-testid={`review-availability-${avail.status}`}>
                <MapPin className="h-4 w-4" />
                {f.pitch_label.trim()} — {avail.status === "orange" ? `${t("avail_orange")} (${avail.moves}×)` : t(AVAIL_KEY[avail.status] || "avail_unknown")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{t("avail_need_dates")}</span>
            )}

            {avail && avail.status === "orange" && avail.chain?.length > 1 && (
              <ul className="mt-2 space-y-1" data-testid="review-move-plan">
                {avail.chain.slice(1).map((seg, i) => (
                  <li key={seg.pitch_label} className="flex items-center gap-1.5 text-xs text-amber-800">
                    <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                    {t("avail_move_on").replace("{date}", fmtDate(seg.from)).replace("{from}", avail.chain[i].pitch_label).replace("{to}", seg.pitch_label)}
                  </li>
                ))}
              </ul>
            )}

            {avail && avail.status === "red" && (
              <div className="mt-2" data-testid="review-alternatives">
                {avail.alternatives?.length ? (
                  <>
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">{t("avail_alts").replace("{zone}", avail.zone || "")}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {avail.alternatives.map((a) => (
                        <button key={a.label} type="button" onClick={() => set("pitch_label", a.label)}
                          className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors"
                          data-testid={`review-alt-${a.label}`}>
                          <MapPin className="h-3 w-3" />{a.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">{t("avail_no_alts")}</p>
                )}
              </div>
            )}
          </div>
        )}

        <Field label={t("notes")}>
          <Textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="review-notes" />
        </Field>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="outline" onClick={onCancel} className="sm:mr-auto" data-testid="review-cancel"><X className="h-4 w-4 mr-1" />{t("cancel")}</Button>
          <Button variant="outline" onClick={askInfo} data-testid="review-ask-info"><MailQuestion className="h-4 w-4 mr-1" />{t("mb_ask_info")}</Button>
          <Button onClick={() => onCreate(build())} data-testid="review-create"><CalendarPlus className="h-4 w-4 mr-1" />{t("mb_make_reservation")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
